<template>
  <v-dialog v-model="dialog" fullscreen hide-overlay persistent no-click-animation transition="dialog-bottom-transition">
    <div class="course-detail-wrapper">
      <div v-if="!courseView" class="loading-container">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        <v-btn text class="mt-4" @click="closeDialog">Abbrechen</v-btn>
      </div>

      <div v-else class="content-container">
        <div class="header-parallax">
          <v-img :src="headerImageSrc" class="header-image" height="45vh">
            <template v-slot:placeholder>
              <v-row class="fill-height ma-0" align="center" justify="center">
                <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
              </v-row>
            </template>
          </v-img>
        </div>

        <v-btn fab small class="fab-back" @click="closeDialog">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <v-btn
          v-if="userService.hasRight('gf_cp_edit', 'gf_cp_cancel') && !schwandenStudio && !niederurnenStudio"
          fab
          small
          class="fab-edit"
          @click="editCourse">
          <v-icon>mdi-pencil</v-icon>
        </v-btn>

        <v-card class="main-content-card mx-auto">
          <div class="main-content-card-wrapper">
            <v-row no-gutters align="center" class="px-4 pt-4">
              <v-col>
                <h1 class="text-h4" style="font-weight: 1000">
                  {{ courseNameFirstPart }}
                  <span class="primary--text">{{ courseNameLastWord }}</span>
                </h1>
              </v-col>
              <v-col cols="auto" class="mb-2">
                <v-btn v-if="userService.hasRight('gf_cp_downloadImage')" icon @click="showMarketingDialog = true">
                  <v-icon>mdi-download-circle-outline</v-icon>
                </v-btn>

                <v-btn icon @click="shareViaWebShare"><v-icon>mdi-export-variant</v-icon></v-btn>
              </v-col>
            </v-row>
            <v-card-text class="course-description py-0">
              <p v-html="truncatedDescription.replace(/\n/g, '<br>')"></p>
            </v-card-text>
            <v-card-actions v-if="courseView.course.beschreibung && courseView.course.beschreibung.length > 150" class="pt-0">
              <v-spacer></v-spacer>
              <v-btn text small color="primary" @click="descriptionExpanded = !descriptionExpanded">
                {{ descriptionExpanded ? "Weniger anzeigen" : "Mehr anzeigen" }}
                <v-icon right>{{ descriptionExpanded ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
              </v-btn>
            </v-card-actions>

            <v-slide-y-transition>
              <v-row v-if="shouldShowClaimButton" no-gutters class="">
                <v-col cols="12">
                  <v-alert text border="left" color="primary" class="d-flex align-center mt-2 font-weight-bold">
                    Dieser Kurs wurde noch nicht abgerechnet.
                    <v-spacer></v-spacer>
                    <v-btn class="mt-4" color="primary" small @click="claimCourseExecutionHours">Jetzt beanspruchen</v-btn>
                  </v-alert>
                </v-col>
              </v-row>
            </v-slide-y-transition>

            <!-- Moderne Detail Cards -->
            <v-slide-y-transition>
              <v-row no-gutters class="mt-4">
                <!-- Uhrzeit Card -->
                <v-col cols="6" class="mb-3">
                  <div class="modern-detail-card mr-2 fill-height">
                    <div class="detail-icon-wrapper time-icon">
                      <v-icon size="24" color="white">mdi-clock-outline</v-icon>
                    </div>
                    <div class="detail-content">
                      <div class="detail-value">
                        {{ courseStartDate(courseView) }} <br />
                        <span class="detail-sublabel">bis {{ courseEndDate(courseView) }} </span>
                      </div>
                    </div>
                  </div>
                </v-col>

                <!-- Ort Card -->
                <v-col cols="6" class="mb-3">
                  <div class="modern-detail-card ml-2 fill-height">
                    <div class="detail-icon-wrapper location-icon">
                      <v-icon size="24" color="white">mdi-map-marker-outline</v-icon>
                    </div>
                    <div class="detail-content">
                      <div class="detail-value">
                        {{ courseView.room ? courseView.room.name : "Studio" }}
                        <span class="detail-sublabel">{{ courseView.studio.name }}</span>
                      </div>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-slide-y-transition>

            <!-- Moderne Trainer Section -->
            <v-slide-y-transition>
              <div class="modern-trainer-section mt-4">
                <div class="trainer-list">
                  <div v-for="(instructor, i) in courseView.instructors" :key="i" class="trainer-item">
                    <div class="trainer-avatar">
                      <v-img
                        :src="
                          instructor.bild
                            ? '/img/profilbilder/' + instructor.bild
                            : 'https://media.istockphoto.com/id/1337144146/de/vektor/standard-avatar-profilsymbolvektor.jpg?s=612x612&w=0&k=20&c=rw3jpsQAdTti6Hh9SAGN15Ex8HYYwmHv3JFHS6KStMo='
                        "></v-img>
                    </div>
                    <div class="trainer-info">
                      <div class="trainer-name">{{ instructor.vorname }} {{ instructor.nachname }}</div>
                      <div class="trainer-role">Kursleiter{{ instructor?.geschlecht === "w" ? "in" : "" }}</div>
                    </div>
                    <v-btn v-if="instructor.telefon" icon class="trainer-action" @click="callPhoneNumber(instructor.telefon)">
                      <v-icon>mdi-phone</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>
            </v-slide-y-transition>

            <v-slide-y-transition
              v-if="courseView.ratings && courseView.ratings.length > 0 && (userService.hasRight('gf_r_viewAll') || isInstructor)">
              <v-card class="modern-trainer-section mt-4" outlined>
                <div class="d-flex align-center section-header">
                  <div class="section-title">Bewertungen ({{ courseView.ratings.length }})</div>
                </div>

                <v-list color="transparent">
                  <template v-for="(rating, index) in courseView.ratings">
                    <v-list-item :key="index + '-rating'">
                      <v-list-item-content>
                        <v-list-item-title class="font-weight-bold"
                          >{{ rating.user.vorname }} {{ rating.user.nachname.charAt(0) }}.</v-list-item-title
                        >
                        <v-rating :value="rating.stars" background-color="amber" color="amber" dense readonly size="18" class="my-1"> </v-rating>
                        <v-list-item-subtitle
                          v-if="rating.message && (rating.stars > 3 || userService.hasRight('gf_r_viewAll'))"
                          style="white-space: normal">
                          "{{ rating.message }}"
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider v-if="index < courseView.ratings.length - 1" :key="index + '-rating-divider'"></v-divider>
                  </template>
                </v-list>
              </v-card>
            </v-slide-y-transition>

            <v-slide-y-transition v-if="userService.hasRight('gf_cp_viewSignups', 'gf_cp_manageSignups') || isInstructor">
              <v-card class="modern-trainer-section mt-4" outlined>
                <div class="d-flex align-center section-header">
                  <div class="section-title">Teilnehmer ({{ courseView.signups.length }})</div>
                  <v-spacer></v-spacer>
                  <v-dialog v-model="dialog2" max-width="500px">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn v-if="canAddSignups" color="#535353" outlined small v-bind="attrs" v-on="on">
                        <v-icon left small>mdi-plus</v-icon> Anmelden
                      </v-btn>
                    </template>
                    <v-card class="modern-dialog-section">
                      <v-toolbar class="section-title" flat>Benutzer anmelden</v-toolbar>
                      <v-divider></v-divider>
                      <user-search
                        :ignoreRights="isInstructor"
                        :hideEmail="!(userService.hasRight('gf_cp_edit') || userService.hasRight('u_search'))"
                        class="pa-4"
                        @update="selectUserIdSignIn"
                        rights="gf_cp_manageSignups"
                        label="Suchen..."></user-search>
                      <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn text @click="cancelUserSignIn">Abbrechen</v-btn>
                        <v-btn color="primary" @click="signInUser(signInUserId)">Anmelden</v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>
                </div>
                <v-list color="transparent" v-if="courseView.signups.length > 0">
                  <template v-for="(signup, index) in courseView.signups">
                    <v-list-item
                      :class="{
                        removeAfter: signup.ts < 1762862400,
                        signupRed: !signup.membershipTitle && !signup.openLeadsFound && signup.membershipTitle !== 'managedByInstructor',
                        signupGreen: signup.membershipTitle && signup.membershipTitle !== 'managedByInstructor',
                        signupYellow: (!signup.membershipTitle || signup.membershipTitle === 'managedByInstructor') && signup.openLeadsFound,
                      }"
                      :key="index + '-signup'"
                      @click="showUserInfo(signup, true)">
                      <v-list-item-avatar>
                        <v-icon v-if="signup.onQueue" color="warning">mdi-alert-circle-outline</v-icon>
                        <v-icon v-else :color="signup.visited ? 'success' : 'grey'">mdi-circle-slice-8</v-icon>
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-subtitle v-if="signup.missedAll > 0">
                          <span :color="getMissingColor(signup.missedAll)">
                            <v-icon>mdi-calendar-remove-outline</v-icon>
                            Kurs: {{ signup.missedSpecificCourse }}, Total: {{ signup.missedAll }}
                          </span>
                        </v-list-item-subtitle>
                        <v-list-item-title>{{ signup.user.vorname }} {{ signup.user.nachname }}</v-list-item-title>
                        <v-list-item-subtitle>Angemeldet am {{ formatSignupDate(signup.ts) }}</v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action class="d-flex flex-row align-center">
                        <v-btn icon @click.stop="signOutUser(signup.user._id)"><v-icon>mdi-account-minus</v-icon></v-btn>
                        <v-btn v-if="!signup.onQueue" icon @click.stop="manageVisited(signup)">
                          <v-icon v-if="signup.visited" :color="'success'">mdi-check-circle</v-icon>
                          <v-icon v-else :color="'grey'">mdi-check-circle-outline</v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>
                    <v-divider v-if="index < courseView.signups.length - 1" :key="index + '-divider'" inset></v-divider>
                  </template>
                </v-list>
                <v-card-text v-else class="text-center grey--text py-4">Noch keine Anmeldungen.</v-card-text>
              </v-card>
            </v-slide-y-transition>

            <v-slide-y-transition>
              <v-card
                v-if="
                  (userService.hasRight('gf_cp_viewSignups', 'gf_cp_manageSignups') || isInstructor) &&
                  courseView.signoffs &&
                  courseView.signoffs.length > 0
                "
                class="modern-trainer-section mt-4"
                outlined>
                <div class="d-flex align-center section-header">
                  <div class="section-title">Abmeldungen ({{ courseView.signoffs.length }})</div>
                </div>

                <v-list color="transparent">
                  <template v-for="(signoff, index) in courseView.signoffs">
                    <v-list-item
                      :class="{
                        removeAfter: signoff.ts < 1762862400,
                        signupRed: !signoff.membershipTitle && !signoff.openLeadsFound && signoff.membershipTitle !== 'managedByInstructor',
                        signupGreen: signoff.membershipTitle && signoff.membershipTitle !== 'managedByInstructor',
                        signupYellow: (!signoff.membershipTitle || signoff.membershipTitle === 'managedByInstructor') && signoff.openLeadsFound,
                      }"
                      :key="index + '-signoff'"
                      @click="showUserInfo(signoff)">
                      <v-list-item-avatar>
                        <v-icon color="error">mdi-circle-slice-8</v-icon>
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title>{{ signoff.user.vorname }} {{ signoff.user.nachname }}</v-list-item-title>
                        <v-list-item-subtitle>Abgemeldet am {{ formatSignupDate(signoff.ts) }}</v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-btn icon @click.stop="signInUser(signoff.user._id)"><v-icon>mdi-account-plus</v-icon></v-btn>
                      </v-list-item-action>
                    </v-list-item>
                    <v-divider v-if="index < courseView.signoffs.length - 1" :key="index + '-divider-signoff'" inset></v-divider>
                  </template>
                </v-list>
              </v-card>
            </v-slide-y-transition>

            <div style="height: 150px"></div>
          </div>
        </v-card>
      </div>

      <div v-if="courseView" class="signup-footer">
        <span>{{ signupCounter() }}</span>
      </div>
    </div>

    <v-dialog v-if="false" persistent max-width="450px">
      <v-card align="center" class="pt-7" v-if="userInformation">
        <v-avatar size="150" v-if="userInformation.user.bild">
          <img v-if="userInformation.user.bild" :src="'/img/profilbilder' + userInformation.user.bild" alt="User Avatar" />
          <v-icon v-else dark>mdi-account-circle</v-icon>
        </v-avatar>
        <v-toolbar class="pt-2" flat color="transparent">
          <v-list-item two-line>
            <v-list-item-content>
              <v-list-item-title class="text-h6"> {{ userInformation.user.vorname }} {{ userInformation.user.nachname }} </v-list-item-title>
              <v-list-item-subtitle>
                {{ userInformation.signupStatus ? "Angemeldet" : "Abgemeldet" }} am {{ formatSignupDate(userInformation.ts) }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-toolbar>
        <v-divider></v-divider>

        <v-list dense class="mx-5">
          <v-list-item
            v-if="(userService.hasRight('gf_cp_edit') || userService.hasRight('u_search')) && userInformation.user.telefon"
            @click="copyText(userInformation.user.telefon)">
            <v-list-item-icon>
              <v-icon>mdi-phone</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ userInformation.user.telefon }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item
            v-if="(userService.hasRight('gf_cp_edit') || userService.hasRight('u_search')) && userInformation.user.email"
            @click="copyText(userInformation.user.email)">
            <v-list-item-icon>
              <v-icon>mdi-email</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ userInformation.user.email }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>

        <div v-if="userInformation.missedDetails && userInformation.missedDetails.length > 0">
          <v-subheader>Anmeldungen ohne Teilnahme</v-subheader>
          <v-expansion-panels flat accordion>
            <v-expansion-panel v-for="(item, index) in reversedMissedDetails" :key="index">
              <v-expansion-panel-header @click="fetchCourseInfoMissingSignup(item)">
                <div>
                  <div class="font-weight-bold">{{ item.course }}</div>
                  <div class="caption grey--text">{{ item.time }}</div>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div v-if="loadingMissingInfo" class="text-center py-4">
                  <v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
                </div>
                <div v-if="!loadingMissingInfo && courseViewMissingInfo && courseViewMissingInfo.item.courseExecutionId === item.courseExecutionId">
                  <v-list dense color="transparent">
                    <v-list-item>
                      <v-list-item-icon><v-icon small>mdi-account-group</v-icon></v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-subtitle>Kursleiter</v-list-item-subtitle>
                        <v-list-item-title>
                          {{ courseViewMissingInfo.courseView.instructors.map((i) => i.vorname).join(", ") }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-icon><v-icon small>mdi-map-marker-radius</v-icon></v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-subtitle>Studio</v-list-item-subtitle>
                        <v-list-item-title>{{ courseViewMissingInfo.courseView.studio.name }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn small outlined color="primary" @click="manageVisitedMissing(findSignupInMissingInfo(item), item)">
                      Als Anwesend markieren
                    </v-btn>
                  </v-card-actions>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
        <v-card-text v-else class="text-center grey--text py-4"> Keine unentschuldigten Abwesenheiten. </v-card-text>

        <v-card-actions align="center" class="pa-4">
          <v-btn class="mx-auto" text color="primary" @click="hideUserInfo">Schliessen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog style="z-index: 99999" v-model="showUserInformation" fullscreen hide-overlay persistent transition="dialog-bottom-transition">
      <v-card v-if="userInformation" class="tiktok-profile-card d-flex flex-column">
        <v-btn icon large class="close-btn mt-4 mr-2" @click="hideUserInfo">
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>

        <v-card-text class="flex-grow-1 text-center pt-12 px-0" style="overflow-y: auto">
          <div class="profile-header mb-8">
            <div class="avatar-container mb-4 mx-auto">
              <v-img
                v-if="userInformation.user.bild"
                :src="'/img/profilbilder' + userInformation.user.bild"
                alt="User Avatar"
                class="profile-image"></v-img>
              <div v-else class="profile-placeholder d-flex align-center justify-center">
                <v-icon size="60" color="white">mdi-account</v-icon>
              </div>
            </div>

            <h2 class="text-h4 font-weight-bold white--text mb-1">{{ userInformation.user.vorname }} {{ userInformation.user.nachname }}</h2>

            <div class="d-inline-flex align-center mt-3 glass-chip px-4 py-1">
              <v-icon x-small left :color="userInformation.signupStatus ? '#4ade80' : '#9ca3af'"> mdi-circle </v-icon>
              <span class="white--text text-subtitle-2">
                {{ userInformation.signupStatus ? "Angemeldet" : "Abgemeldet" }}
              </span>
              <span class="grey--text text--lighten-1 ml-2 caption">
                {{ formatSignupDate(userInformation.ts) }}
              </span>
            </div>
          </div>

          <div class="action-grid px-6 mb-8">
            <v-row dense>
              <v-col cols="12" v-if="(userService.hasRight('gf_cp_edit') || userService.hasRight('u_search')) && userInformation.user.telefon">
                <v-btn block x-large color="primary" class="modern-action-btn elevation-0" @click="callPhoneNumber(userInformation.user.telefon)">
                  <v-icon left>mdi-phone</v-icon>
                  Anrufen
                </v-btn>
              </v-col>

              <v-col cols="6" v-if="(userService.hasRight('gf_cp_edit') || userService.hasRight('u_search')) && userInformation.user.telefon">
                <v-btn
                  block
                  large
                  color="rgba(255,255,255,0.1)"
                  class="modern-action-btn glass-btn white--text"
                  @click="copyText(userInformation.user.telefon)">
                  <v-icon left small>mdi-content-copy</v-icon>
                  Nr. Kopieren
                </v-btn>
              </v-col>

              <v-col cols="6" v-if="(userService.hasRight('gf_cp_edit') || userService.hasRight('u_search')) && userInformation.user.email">
                <v-btn
                  block
                  large
                  color="rgba(255,255,255,0.1)"
                  class="modern-action-btn glass-btn white--text"
                  @click="copyText(userInformation.user.email)">
                  <v-icon left small>mdi-email</v-icon>
                  Mail Kopieren
                </v-btn>
              </v-col>
            </v-row>
          </div>

          <v-divider class="mx-8 mb-6" style="border-color: rgba(255, 255, 255, 0.1)"></v-divider>

          <div class="text-left px-4 mx-auto" style="max-width: 600px">
            <div v-if="userInformation.missedDetails && userInformation.missedDetails.length > 0">
              <div class="section-label mb-4 pl-2">Offene Abwesenheiten</div>

              <v-expansion-panels accordion flat dark class="glass-panels">
                <v-expansion-panel v-for="(item, index) in reversedMissedDetails" :key="index" class="mb-3 rounded-lg">
                  <v-expansion-panel-header @click="fetchCourseInfoMissingSignup(item)" class="glass-header">
                    <div class="d-flex align-center">
                      <v-avatar size="40" color="rgba(244, 67, 54, 0.2)" class="mr-4 rounded-lg">
                        <v-icon color="#f44336" small>mdi-calendar-remove</v-icon>
                      </v-avatar>
                      <div class="d-flex flex-column">
                        <span class="font-weight-bold white--text text-body-1">{{ item.course }}</span>
                        <span class="caption grey--text text--lighten-1">{{ item.time }}</span>
                      </div>
                    </div>
                  </v-expansion-panel-header>

                  <v-expansion-panel-content class="glass-content pt-4">
                    <div v-if="loadingMissingInfo" class="text-center py-2">
                      <v-progress-circular indeterminate color="primary" size="20"></v-progress-circular>
                    </div>

                    <div
                      v-if="!loadingMissingInfo && courseViewMissingInfo && courseViewMissingInfo.item.courseExecutionId === item.courseExecutionId">
                      <div class="d-flex align-center mb-3 px-2">
                        <v-icon small color="grey" class="mr-3">mdi-account</v-icon>
                        <span class="text-body-2 grey--text text--lighten-2">
                          {{ courseViewMissingInfo.courseView.instructors.map((i) => i.vorname).join(", ") }}
                        </span>
                      </div>

                      <v-btn
                        block
                        color="white"
                        light
                        depressed
                        class="rounded-lg font-weight-bold"
                        @click="manageVisitedMissing(findSignupInMissingInfo(item), item)">
                        <v-icon left small>mdi-check-circle</v-icon>
                        Als Anwesend markieren
                      </v-btn>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>

            <div v-else class="d-flex flex-column align-center justify-center py-12 opacity-60">
              <div class="empty-state-circle mb-4">
                <v-icon size="32" color="white">mdi-check</v-icon>
              </div>
              <span class="grey--text text--lighten-1">Alles erledigt</span>
            </div>
          </div>

          <div style="height: 100px"></div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showMarketingDialog" persistent max-width="320">
      <v-card dark>
        <v-card-title class="text-h5 pt-5 pb-5">Social Media Material</v-card-title>
        <v-card-text> Lade hier Material für Instagram / Facebook und Whatsapp Stories, sowie Beiträge herunter </v-card-text>
        <v-card-actions class="d-flex flex-column">
          <v-btn block class="mb-3" color="primary" @click="downloadMarketingImage('square')"> Posts (Quadratisch) </v-btn>
          <v-btn block class="mb-3" color="primary" @click="downloadMarketingImage('vertical')"> Stories (Hochformat) </v-btn>
          <v-btn block class="mb-3" color="primary" @click="downloadPrint">A4 PDF Druckversion</v-btn>
          <v-btn text @click="showMarketingDialog = false">Schliessen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="lastCheckinAllowedDialog" persistent max-width="500">
      <v-card dark>
        <v-card-title class="primary white--text text-h6">Wichtige Information!</v-card-title>
        <v-card-text class="mt-4">
          Hallo {{ userService.getUserFirstName() }}, <br /><br />
          wir konnten leider keinen Vertrag unter der E-Mail-Adresse finden, mit der du dich in Roody registriert hast. Es kann sein, dass die
          E-Mail-Adressen nicht übereinstimmen.
          <br /><br />
          Bitte beachte, dass du dich nur noch <strong>dieses Mal</strong> ohne gültigen Vertrag für einen Kurs anmelden kannst. <br /><br />
          Bitte kläre dies bei Gelegenheit an der Theke in deinem Fitnesscenter. Vielen Dank für dein Verständnis.
        </v-card-text>
        <v-card-actions class="justify-center pb-4">
          <v-btn color="primary" @click="lastCheckinAllowedDialog = false">Gelesen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="checkinNotAllowedDialog" persistent max-width="500">
      <v-card dark>
        <v-card-title class="primary white--text text-h6">Wichtige Information!</v-card-title>
        <v-card-text class="mt-4">
          Hallo {{ userService.getUserFirstName() }}, <br /><br />
          eine weitere Anmeldung ist ohne aktiven Vertrag leider nicht möglich, da du dich bereits einmalig ohne gültigen Vertrag angemeldet hast.
          <br /><br />
          Bitte überprüfe deine Vertragsdaten oder kläre dies an der Theke in deinem Fitnesscenter. Unsere Mitarbeiter stehen dir gerne zur Verfügung.
          Vielen Dank.
        </v-card-text>
        <v-card-actions class="justify-center pb-4">
          <v-btn color="primary" @click="checkinNotAllowedDialog = false">Gelesen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import api from "../../api"
import userService from "../../services/userService"
import { mapGetters, mapActions, mapState } from "vuex"
import userSearch from "../../components/userSearch.vue"
import { habitService } from "@/services/habitService.js"
import { saveAs } from "file-saver"

export default {
  name: "courseListView",
  components: { userSearch },
  data: () => ({
    loadingMissingInfo: false,
    dialog: true,
    dialog2: false,
    descriptionExpanded: false,
    courseView: null,
    signedUp: null,
    onQueue: null,
    isInstructor: false,
    signInUserId: null,
    fullyLoaded: false,
    userService,
    showUserInformation: false,
    userInformation: null,
    courseViewMissingInfo: null,
    showMarketingDialog: false,
    nowTimer: null,
    now: Math.round(Date.now() / 1000),
    shareText: "Hey! Lust, mich beim nächsten Mal zu begleiten? Schau dir diesen Kurs an:",
    lastCheckinAllowedDialog: false,
    checkinNotAllowedDialog: false,
    activeContract: false,
  }),

  computed: {
    ...mapGetters("navigationStoreNotPersisted", ["navigationBarTextState"]),
    ...mapState("studioStore", ["selectedStudio"]),
    ...mapGetters("userStore", ["activeContractDetails"]),
    courseNameFirstPart() {
      if (!this.courseView?.course?.name) {
        return ""
      }
      const words = this.courseView.course.name.split(" ")
      // NEUE Bedingung: Wenn es nur ein Wort (oder weniger) ist...
      if (words.length <= 1) {
        return this.courseView.course.name // ...gib den ganzen Namen zurück.
      }
      words.pop()
      return words.join(" ")
    },

    /**
     * ANGEPASST: Berechnet das letzte Wort des Kursnamens.
     * Gibt nur dann ein Wort zurück, wenn es mehr als eins im Namen gibt.
     */
    courseNameLastWord() {
      if (!this.courseView?.course?.name) {
        return ""
      }
      const words = this.courseView.course.name.split(" ")
      // NEUE Bedingung: Wenn es nur ein Wort (oder weniger) ist...
      if (words.length <= 1) {
        return "" // ...gib einen leeren String zurück.
      }
      return words[words.length - 1]
    },
    truncatedDescription() {
      // NEU
      const text = this.courseView?.course?.beschreibung
      if (!text) return ""
      // Kürze den Text, wenn er nicht ausgeklappt ist und länger als 150 Zeichen
      if (!this.descriptionExpanded && text.length > 150) {
        return text.substring(0, 150) + "..."
      }
      return text
    },

    headerImageSrc() {
      return this.courseView?.course?.bild ? `/img/kurse/${this.courseView.course.bild}` : ""
    },
    schwandenStudio() {
      return this.selectedStudio?.code === "schwanden"
    },
    niederurnenStudio() {
      return this.selectedStudio?.code === "niederurnen"
    },
    canAddSignups() {
      if (!this.courseView) return false
      return this.userService.hasRight("gf_cp_viewSignups", "gf_cp_manageSignups") || (this.isInstructor && this.$moment().isAfter(this.signUpLimit))
    },
    reversedMissedDetails() {
      if (!this.userInformation || !this.userInformation.missedDetails) return []
      return [...this.userInformation.missedDetails].reverse()
    },
    shouldShowClaimButton() {
      // 1. Define the studios with the lower threshold
      const lowerThresholdStudios = ["arbon", "oerlikon", "herisau", "sirnach", "wetzikonnord"]

      const noTresholdStudios = ["muri", "kreuzlingen", "frauefeldwest"]
      // 2. Determine the required amount based on the studio code
      const minRequiredSignups = noTresholdStudios.includes(this.selectedStudio?.code)
        ? 0
        : lowerThresholdStudios.includes(this.selectedStudio?.code)
        ? 2
        : 3

      // --- EARLY EXITS ---

      if (!this.courseView || !this.courseView.past || this.courseView.cancelled) {
        return false
      }

      // Check signups (Instructors fail here if low signups, Admins with right can pass)
      const hasEnoughSignups = this.courseView.signups.length > minRequiredSignups
      const canAdminOverrideSignups = this.userService.hasRight("gf_claimforinstructor")

      if (!hasEnoughSignups && !canAdminOverrideSignups) {
        return false
      }

      // Check generic "not claim" right
      if (this.userService.hasRight("gf_not_claim") && !this.userService.hasRight("gf_claimSelf")) {
        return false
      }

      // --- DATE LOGIC (ENFORCED FOR EVERYONE) ---

      const claimDeadline = this.$moment(this.courseView.ts * 1000)
        .add(1, "months")
        .startOf("month")
      // Optional: .endOf('day') anhängen, wenn man den ganzen 1. Tag Zeit haben soll.

      const isOnTime = this.$moment().isSameOrBefore(claimDeadline)

      if (!isOnTime && !this.userService.hasRight("gf_claimAdmin")) {
        // Hier wird jetzt hart abgebrochen, egal wer der User ist
        return false
      }

      // --- FINAL CHECK ---

      // Refactored: We already checked signups & date above.
      // Now we just check if they are the instructor OR have the admin right.
      // Note: I replaced the hardcoded '> 2' with the dynamic variable logic implicitly via earlier checks.

      const isEligibleToClaim = this.isInstructor || canAdminOverrideSignups

      return this.atLeastOneInstructor && isEligibleToClaim && !this.courseView.hoursClaimed
    },
    atLeastOneInstructor() {
      return !!this.courseView?.instructors?.[0]?._id
    },
  },

  async created() {
    await this.fetchCourseView()
    this.nowTimer = setInterval(() => {
      this.now = Math.round(Date.now() / 1000)
    }, 1000)
    if (!window.location.href.includes("local")) {
      let hasContract = await api.fetchTACMemberHasExistingContractByEmail()
      this.activeContract = this.$moment(hasContract.date, "YYYY-MM-DD").isSameOrAfter(this.$moment())
    }
  },

  mounted() {
    setTimeout(() => {
      this.fullyLoaded = true
    }, 200)
  },

  beforeDestroy() {
    clearInterval(this.nowTimer)
    this.setNavigationBarText(null)
  },

  methods: {
    ...mapActions("navigationStoreNotPersisted", ["setNavigationBarText"]),
    ...mapActions("navigationStore", ["setSelectedNavigation"]),
    getMissingColor(amount) {
      if (amount <= 3) return "#cdcdcd00"
      if (amount <= 6) return "#cdcdcd00"
      if (amount > 6) return "#cdcdcd00"
    },
    async fetchCourseView() {
      try {
        let ts = this.$route.params.ts || 0
        const courseData = await api.fetchCourseView(this.$route.params.id, ts)

        this.courseView = courseData.courseView
        this.signedUp = courseData.signedUp
        this.onQueue = courseData.onQueue
        this.isInstructor = courseData.isInstructor

        this.signUpLimit = courseData.signUpLimit

        this.updateNavigationBar()

        if (this.courseView._id !== this.$route.params.id) {
          if (this.$route.query.home) {
            this.$router.push("/kurse/" + this.courseView._id + "?home=true")
          } else {
            this.$router.push("/kurse/" + this.courseView._id)
          }
        }
      } catch (e) {
        this.$toast.error("Kurs konnte nicht geladen werden.")
        this.$router.push("/kurse")
      }
    },

    updateNavigationBar() {
      if (!this.courseView) return
      const { past, current, disableSignup, disableSignoff, reqSignup, cancelled, signups, amount } = this.courseView
      if ((disableSignup && !this.signedUp) || (disableSignoff && this.signedUp) || past || current || !reqSignup || cancelled) {
        this.setNavigationBarText({ text: "ZURÜCK", state: false })
      } else {
        const onWaitingList = this.onQueue || (!this.signedUp && amount <= signups.length)
        this.setNavigationBarText({
          false: onWaitingList ? "IN WARTELISTE EINTRAGEN" : "ANMELDEN",
          true: "ABMELDEN",
          state: this.signedUp,
        })
      }
    },

    signupCounter() {
      if (!this.courseView) return ""
      const { past, current, disableSignup, disableSignoff, reqSignup, cancelled, amount, signups } = this.courseView
      if ((disableSignup && !this.signedUp) || (disableSignoff && this.signedUp) || past || current || !reqSignup || cancelled) {
        return ""
      }
      if (signups) {
        let freeSlots = amount - signups.length
        if (freeSlots === 1) return "Noch 1 Platz verfügbar!"
        if (freeSlots > 0) return `Noch ${freeSlots} Plätze verfügbar!`
        return "Warteliste aktiv"
      }
      return `${amount} Plätze verfügbar`
    },

    closeDialog() {
      this.setSelectedNavigation(null)
      this.setNavigationBarText(null)
      this.dialog = false
      this.$nextTick(() => {
        this.setSelectedNavigation("groupFitness")
        if (this.$router.history?.current?.query?.hasOwnProperty("event")) {
          this.$router.go(-1)
        } else {
          if (this.$route.query.home) {
            this.$router.push("/")
          } else {
            this.$router.push("/kurse")
          }
        }
      })
    },

    courseEndDate(course) {
      return this.$moment().set({ hours: course.hours, minutes: course.minutes }).add(course.duration, "minutes").format("HH:mm")
    },

    courseStartDate(course) {
      return this.$moment().set({ hours: course.hours, minutes: course.minutes }).format("HH:mm")
    },

    formatSignupDate(ts) {
      return this.$moment.unix(ts).format("DD.MM.YY HH:mm")
    },

    shareViaWebShare() {
      if (navigator.share) {
        navigator.share({
          title: this.courseView.course.name,
          text: this.shareText,
          url: window.location.href,
        })
      } else {
        this.$toast.info("Sharing wird von diesem Browser nicht unterstützt.")
      }
    },

    editCourse() {
      this.$router.push(`/kurse/${this.courseView._id}/edit`)
    },

    async manageSignUp(signUpStatus) {
      try {
        let contractTitle = this.activeContractDetails?.title || ""
        contractTitle = encodeURI(contractTitle)

        const response = await api.manageCourseSignup(this.courseView._id, signUpStatus, contractTitle)
        sessionStorage.removeItem("homeCourseData")
        if (response.simulatenousSignup) {
          this.$toast.error("Du kannst dich nicht für mehrere Kurse gleichzeitig anmelden")
        }
        if (response.toManyEventSignups) {
          alert("Du hast dich bereits für zu viele Kurse dieses Events angemeldet.")
        }
        this.fetchCourseView()
        this.$emit("updateCourse")
      } catch (error) {
        console.log(error)
        this.$toast.error("Aktion fehlgeschlagen.")
      }
    },

    async fetchCourseInfoMissingSignup(item) {
      // Wenn die Details für diesen Kurs schon geladen sind, nichts tun
      if (this.courseViewMissingInfo && this.courseViewMissingInfo.item.courseExecutionId === item.courseExecutionId) {
        return
      }

      this.loadingMissingInfo = true
      this.courseViewMissingInfo = null // Alte Daten zurücksetzen
      try {
        const infos = await api.fetchCourseView(item.courseExecutionId, 0)
        this.courseViewMissingInfo = infos
        this.courseViewMissingInfo.item = item
      } catch (e) {
        this.$toast.error("Details konnten nicht geladen werden.")
      } finally {
        this.loadingMissingInfo = false
      }
    },

    findSignupInMissingInfo(item) {
      if (!this.courseViewMissingInfo || !this.userInformation) return null

      // Finde das Signup-Objekt des aktuellen Benutzers in den Details des verpassten Kurses
      return this.courseViewMissingInfo.courseView.signups.find((s) => s.user._id === this.userInformation.user._id)
    },

    async claimCourseExecutionHours() {
      try {
        if (this.isInstructor) {
          await api.claimCourseExecutionHours(this.courseView._id)
        } else if (this.atLeastOneInstructor) {
          await api.claimCourseExecutionHoursForInstructor(this.courseView._id, this.courseView.instructors[0]._id)
        } else {
          throw new Error("Kein Trainer für die Beanspruchung gefunden.")
        }
        this.fetchCourseView()
        this.$toast.success("Stunde wurde erfolgreich beansprucht")
      } catch (e) {
        this.courseView.hoursClaimed = false
        this.$toast.error("Es gab einen Fehler. Bitte versuche es später erneut.")
      }
      this.$emit("updateCourse")
    },

    callPhoneNumber(tel) {
      window.location.href = `tel:${tel}`
    },

    copyText(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$toast.success("In Zwischenablage kopiert!")
      })
    },

    async addAsHabit() {
      let gfHabit = habitService.getNewGroupFitnessHabit()
      gfHabit.courseTimeline = this.courseView.courseTimelineId
      if (await gfHabit.save()) {
        this.$toast.success("Als Gewohnheit hinzugefügt.")
      }
    },

    showUserInfo(userInformation, signup = false) {
      this.userInformation = { ...userInformation, signupStatus: signup }
      this.showUserInformation = true
    },

    hideUserInfo() {
      this.showUserInformation = false
      this.userInformation = null
    },

    async downloadPrint() {
      try {
        this.$toast.info("Download wird vorbereitet...")
        await api.downloadA4PDFGF(this.$route.params.id)
      } catch (error) {
        this.$toast.error("Download fehlgeschlagen.")
      }
    },

    async downloadMarketingImage(format) {
      try {
        const data = await api.downloadMarketingImageGF(
          format,
          this.$vuetify.theme.themes.light.primary.toString().replace("#", ""),
          this.$route.params.id,
        )
        if (window.flutter_inappwebview) {
          const reader = new FileReader()
          reader.readAsDataURL(data)
          reader.onloadend = () => {
            const base64 = reader.result.split(",")[1]
            window.flutter_inappwebview.callHandler("blobToBase64Handler", base64, "png", "SocialMedia")
          }
        } else {
          saveAs(data, `Marketing_${format}_${Date.now()}`)
        }
      } catch (error) {
        this.$toast.error("Download fehlgeschlagen.")
      }
    },

    manageVisited(signup) {
      signup.visited = !signup.visited
      api.manageSignupVisited(signup._id, signup.visited ? "1" : "0")
    },

    async manageVisitedMissing(signup, itemToRemove) {
      // 1. Die Methode akzeptiert jetzt 'itemToRemove'
      // Wenn kein gültiges Signup-Objekt gefunden wird, abbrechen
      if (!signup) {
        this.$toast.error("Fehler: Teilnehmer konnte nicht zugeordnet werden.")
        return
      }

      // Die Bestätigung kann bleiben, ist aber optional
      if (!confirm("Person als anwesend markieren?")) return

      try {
        signup.visited = true
        await api.manageSignupVisited(signup._id, "1")

        // 2. WICHTIG: Eintrag aus der lokalen Liste entfernen für sofortiges UI-Update
        const index = this.userInformation.missedDetails.findIndex((detail) => detail.courseExecutionId === itemToRemove.courseExecutionId)
        if (index > -1) {
          this.userInformation.missedDetails.splice(index, 1)
        }

        // Der Rest kann bleiben, um die Hintergrund-Daten zu synchronisieren
        this.fetchCourseView()
        this.courseViewMissingInfo = null
        this.$toast.success("Aktualisiert")
      } catch (e) {
        this.$toast.error("Es gab einen Fehler.")
      }
    },

    cancelUserSignIn() {
      this.dialog2 = false
      this.signInUserId = null
    },

    async signOutUser(userId) {
      await api.manageUserCourseSignup(this.courseView._id, false, userId)
      this.$emit("updateCourse")
      this.fetchCourseView()
    },

    async signInUser(userId) {
      if (!userId) return
      await api.manageUserCourseSignup(this.courseView._id, true, userId)
      this.$emit("updateCourse")
      this.fetchCourseView()
      this.cancelUserSignIn()
    },

    selectUserIdSignIn(user) {
      this.signInUserId = user
    },
  },

  watch: {
    navigationBarTextState(value) {
      if (value === null || !this.courseView) return

      const { past, current, disableSignup, disableSignoff, reqSignup, cancelled } = this.courseView
      const isActionDisabled = (disableSignup && !this.signedUp) || (disableSignoff && this.signedUp) || past || current || !reqSignup || cancelled

      if (isActionDisabled) {
        if (value) this.closeDialog()
      } else {
        if (value !== this.signedUp) {
          this.manageSignUp(value)
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.action-grid {
  max-width: 500px;
  margin: auto;
}
.tiktok-profile-card {
  // Moderner Deep-Dark Hintergrund (kein flaches #111827 mehr)
  background: linear-gradient(to bottom, #1e1e24 0%, #000000 100%) !important;
  color: white;
  position: relative;
}

.close-btn {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 10;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
}

// Avatar
.avatar-container {
  width: 120px;
  height: 120px;
  position: relative;

  .profile-image {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 3px solid #000; // Clean border
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.2); // Outer glow ring
    object-fit: cover;
  }

  .profile-placeholder {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: linear-gradient(135deg, #383838 0%, #1f1f1f 100%);
    border: 3px solid rgba(255, 255, 255, 0.1);
  }
}

// Badge
.glass-chip {
  background: rgba(255, 255, 255, 0.08);
  border-radius: 50px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(4px);
}

// Action Buttons
.modern-action-btn {
  border-radius: 16px; // Modernes "Squircle" oder stark abgerundet
  text-transform: none; // Kein ALL CAPS, wirkt freundlicher
  font-weight: 700;
  letter-spacing: 0.5px;
  font-size: 1rem;

  &.glass-btn {
    background: rgba(255, 255, 255, 0.1) !important;
    border: 1px solid rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(10px);

    &:hover {
      background: rgba(255, 255, 255, 0.2) !important;
    }
  }
}

// Section Label
.section-label {
  text-transform: uppercase;
  letter-spacing: 1.2px;
  font-size: 0.75rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.5);
}

// Expansion Panels (Glassmorphism)
.glass-panels {
  .v-expansion-panel {
    background: rgba(255, 255, 255, 0.05) !important;
    border: 1px solid rgba(255, 255, 255, 0.05);

    &::before {
      display: none;
    } // Schatten entfernen
  }

  .glass-header {
    background: transparent;
  }

  .glass-content .v-expansion-panel-content__wrap {
    padding: 0 16px 16px 16px;
  }
}

// Empty State Icon
.empty-state-circle {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
}
// SCSS für den "Glow"-Statusbalken bei Teilnehmern
.removeAfter {
  &::after {
    display: none !important;
  }
}
.signupGreen,
.signupRed,
.signupYellow {
  // Setzt den Positionierungskontext für den ::before-Balken
  position: relative !important;
  // Verhindert, dass der Glow-Effekt ggf. über Ränder hinaus "blutet"
  overflow: hidden;
  // WIR ENTFERNEN HIER DAS "padding-left: 12px !important;"

  &::after {
    content: "";
    position: absolute;
    left: 0;
    width: 4px; // Breite des Balkens

    // Lässt den Balken in der Mitte schweben (80% Höhe)
    top: 10%;
    bottom: 10%;

    // Rundet die Ecken auf der rechten Seite ab
    border-radius: 0 3px 3px 0;
    transition: all 0.3s ease;
    z-index: 1; // Sicherstellen, dass er über dem Avatar liegt
  }

  // *** DAS IST DIE WICHTIGE ÄNDERUNG ***
  // Wir zielen auf den Avatar (das Icon) direkt *innerhalb*
  // der Status-Items und geben ihm einen linken Abstand.
  > .v-list-item__avatar {
    margin-left: 12px !important; // 4px für den Balken + 8px Luft
  }
}

.signupGreen {
  &::after {
    background-color: #4caf50; // Ein kräftiges Grün
    box-shadow: 0 0 10px 2px rgba(76, 175, 80, 0.6); // Grüner "Glow"
  }
}

.signupRed {
  &::after {
    background-color: #f44336; // Ein kräftiges Rot
    box-shadow: 0 0 10px 2px rgba(244, 67, 54, 0.6); // Roter "Glow"
  }
}

.signupYellow {
  &::after {
    background-color: #ffc107; // Ein kräftiges Gelb/Orange
    box-shadow: 0 0 10px 2px rgba(255, 193, 7, 0.6); // Gelber "Glow"
  }
}

.v-list-item__avatar:first-child {
  margin-right: 2px; // Abstand zwischen Avatar und Titel
}
// Kurstitel hervorheben
h1.text-h4 {
  color: #111827; // Fast schwarz
  text-shadow: none; // Schatten entfernen, wirkt auf hell sauberer
}

// Beschreibungstext
.course-description {
  color: #4b5563; // Dunkles Grau für Fließtext
}

// Titel der Unterkarten
.sub-card .v-card__title {
  color: #374151;
  font-weight: 500;
}
.v-list-item {
  padding: 0px;
}
// Listeneinträge
.v-list-item__title {
  color: #1f2937; // Dunkles Grau, fast schwarz
}

.v-list-item__subtitle,
.grey--text {
  color: #6b7280; // Helleres Grau für Untertitel
}

// Icons in den Listen-Aktionen
.v-list-item__action .v-btn {
  color: #9ca3af;
}
.main-content-card-wrapper {
  max-width: 500px;
  margin: auto;
  padding: 20px 10px;
}
// Neuer Ansatz mit Gradient-Hintergrund und starker Akzentfarbe
.course-detail-wrapper {
  // Ein dunkler, aber nicht rein schwarzer Hintergrund.
  // Ein subtiler Gradient macht es lebendiger.
  background: #111827; // Ein tiefes Navy-Blau
  height: 100vh;
  width: 100vw;
  overflow-y: auto;
  // NEU HINZUFÜGEN:

  // Scrollbar ausblenden für cleanen Look
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.loading-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  background-color: var(--v-appBackground);
}

.header-parallax {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 0;
  overflow: hidden;
  .header-image::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    // Ein einfacherer Verlauf, der nur abdunkelt, damit der Hintergrund durchscheint
    // background: linear-gradient(to top, var(--v-appBackground, #f7f8fa) 20%, transparent 80%);
  }
}

.fab-back {
  position: fixed;

  z-index: 10;

  top: 16px;
  // Braucht einen Hintergrund, um vor dem Bild sichtbar zu sein
  background-color: white !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
.fab-edit {
  position: fixed;

  z-index: 10;

  top: 16px;
  // Der primäre Button kann so bleiben, Kontrast ist gut
  color: white;
  background-color: var(--v-primary) !important;
}

.fab-back {
  left: 16px;
}
.fab-edit {
  right: 16px;
}

.main-content-card {
  // 1. Hintergrund deckender machen (damit man nicht merkt, dass der Blur fehlt)
  background-color: #f9fafb !important; // Oder rgba(255, 255, 255, 0.98)

  // 2. WICHTIG: Diese Zeilen MÜSSEN weg oder auskommentiert werden!
  // -webkit-backdrop-filter: blur(20px);
  // backdrop-filter: blur(20px);

  // 3. Rand etwas sichtbarer machen als Ersatz für den fehlenden Blur-Look
  border: 1px solid rgba(0, 0, 0, 0.05);

  // 4. Den Schatten beibehalten, aber sicherstellen, dass er nicht zu riesig ist
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);

  // Rest bleibt gleich...
  color: #374151;
  margin-top: 40vh;
  min-height: 60vh;
  border-top-left-radius: 24px !important;
  border-top-right-radius: 24px !important;
  border-bottom-left-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
  position: relative;
  z-index: 1;

  // 5. GPU-Hack auch direkt auf der Karte anwenden
  transform: translateZ(0);
}
/* Moderne Detail Cards */
.modern-detail-card {
  display: flex;
  align-items: center;
  padding: 20px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.8) 100%);

  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.15);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px 0 rgba(31, 38, 135, 0.25);
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.08) 100%);
  }
}

.detail-icon-wrapper {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;

  &.time-icon {
    background: linear-gradient(135deg, #02734a 0%, #a3c6c4 100%);
  }

  &.location-icon {
    background: linear-gradient(135deg, #1c4966 0%, #a3c6c4 100%);
  }
}

.detail-content {
  flex: 1;

  .detail-label {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #9ca3af;
    margin-bottom: 4px;
    font-weight: 600;
  }

  .detail-value {
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
    line-height: 1.2;
  }

  .detail-sublabel {
    display: block;
    font-size: 14px;
    color: #6b7280;
    font-weight: 400;
    margin-top: 2px;
  }
}

/* Moderne Trainer Section */
.modern-trainer-section {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.8) 100%);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  padding: 24px 16px;
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.1);
}
.modern-dialog-section {
  background: #ffffff;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  padding: 24px;
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.1);

  .section-title {
    font-size: 14px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    background: transparent;
    margin-right: 16px;
    white-space: nowrap;
  }
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 0px;

  .section-title {
    font-size: 14px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1.5px;

    margin-right: 16px;
    white-space: nowrap;
  }

  .section-line {
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, #e5e7eb 0%, transparent 100%);
  }
}

.trainer-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.trainer-item {
  display: flex;
  align-items: center;

  border-radius: 12px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.8);
    transform: translateX(4px);
  }
}

.trainer-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 16px;
  border: 3px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.trainer-info {
  flex: 1;

  .trainer-name {
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
    line-height: 1.2;
  }

  .trainer-role {
    font-size: 13px;
    color: #9ca3af;
    margin-top: 2px;
  }
}

.trainer-action {
  color: #6b7280 !important;
  transition: all 0.3s ease;

  &:hover {
    color: var(--v-primary-base) !important;
    transform: scale(1.1);
  }
}

/* Animation Keyframes */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modern-detail-card,
.modern-trainer-section {
  animation: slideIn 0.5s ease-out;
  animation-fill-mode: both;
}

.modern-detail-card:nth-child(2) {
  animation-delay: 0.1s;
}

.trainer-item {
  animation: slideIn 0.5s ease-out;
  animation-fill-mode: both;
}

.trainer-item:nth-child(1) {
  animation-delay: 0.2s;
}
.trainer-item:nth-child(2) {
  animation-delay: 0.3s;
}
.trainer-item:nth-child(3) {
  animation-delay: 0.4s;
}

// Sub-cards bleiben wie sie waren, nur leicht angepasst
.sub-card {
  // Statt transparent geben wir ihnen einen leicht anderen Weißton, um sie abzugrenzen
  background: rgba(255, 255, 255, 0.5) !important;
  border-radius: 16px !important;
  height: 100%;
  // Ein etwas sichtbarer Rand als bei der Hauptkarte
  border: 1px solid rgba(0, 0, 0, 0.1) !important;

  // Die Listen innerhalb der Karten bekommen transparenten Hintergrund
  .v-list {
    background-color: transparent !important;
  }
}

.signup-footer {
  position: fixed;
  bottom: 80px;
  left: 0;
  right: 0;
  text-align: center;

  font-weight: 500;
  pointer-events: none;
  text-shadow: 1px 1px 2px #090a0f;
}
// Footer mit dem Zähler
.signup-footer span {
  font-weight: 500;
  color: #374151; // Dunkler Text
  text-shadow: none; // Kein Schatten nötig
}

// Alert-Box anpassen
</style>
